import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {SingleValue} from 'react-select';
import {ordersApi, supportingDataApi, marketDataStalenessAPI} from '@/services/api';
import {formatCurrency, formatDateTime, formatPrice} from '@/utils/formatters';
import { getSelectedGroupValue as getSchemeGroupValue, isGlobalSelection } from '@/utils/orderSchemeGroup';
import { joinFieldErrors } from '@/utils/apiErrors';
import OrderSchemeSelector from '@/components/order_management/OrderSchemeSelector';
import OrderClientSelector from '@/components/order_management/OrderClientSelector';
import { useSchemeClients, clientIdsForScheme, pruneClientIds } from '@/hooks/useSchemeClients';
import FormCard, {
    FormSection,
    FormGrid,
    FormField,
    FormInput,
    FormSelect,
    FormReactSelect,
    FormHintError,
    SelectOption,
} from '@/components/utils/FormCard';
import {Scheme, Company, LivePrice, ORDER_CALC_TYPE_MUTUAL_FUND} from '@/types/client';
import formDefaults from '@/config/formDefaults.json';

interface BuyOrderFormData {
    scheme_ids: number[];
    company_id: number;
    cl1: number;
    cl2: number;
    increase_to_1: number;
    increment_max_1: number;
    increase_to_2: number;
    increment_max_2: number;
    increase_by_1: number;
    increase_by_2: number;
    limit_price: number;
    max_holding: number;
    /** Null until the user types one; the region's default applies until then. */
    min_order_val: number | null;
    buy_method: number;
    cash_utilization_buffer_pct: number;
    median_volume_pct: number;
    apply_volume_cap: boolean;
    /** Client-wise selection; a scheme with none of its clients picked orders for all of them. */
    client_ids: number[];
}

const BuyOrderForm: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<BuyOrderFormData>({
        scheme_ids: [],
        company_id: 0,
        cl1: formDefaults.buyOrderForm.targetParameters.cl1,
        cl2: formDefaults.buyOrderForm.targetParameters.cl2,
        increase_to_1: 0,
        increment_max_1: 0,
        increase_to_2: 0,
        increment_max_2: 0,
        increase_by_1: 0,
        increase_by_2: 0,
        max_holding: formDefaults.buyOrderForm.limits.max_holding,
        min_order_val: null,
        limit_price: 0,
        buy_method: 1,
        cash_utilization_buffer_pct: formDefaults.buyOrderForm.cashUtilization.cash_utilization_buffer_pct,
        median_volume_pct: formDefaults.buyOrderForm.medianVolume.median_volume_pct,
        apply_volume_cap: formDefaults.buyOrderForm.medianVolume.apply_volume_cap,
        client_ids: [],
    });

    const [schemes, setSchemes] = useState<Scheme[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    // LivePrice, not main's inline shape — the currency badge reads currency_code off it.
    const [livePrice, setLivePrice] = useState<LivePrice | null>(null);
    const [livePriceLoading, setLivePriceLoading] = useState(false);
    const userEditedLimitPrice = useRef(false);
    const userEditedCashBuffer = useRef(false);

    const clientWiseSchemeIds = useMemo(
        () => formData.scheme_ids.filter(id => schemes.find(s => s.id === id)?.allow_client_wise_orders),
        [formData.scheme_ids, schemes]
    );
    const { schemeClients, loading: clientsLoading } = useSchemeClients(clientWiseSchemeIds);

    // Null only when a scheme payload arrives without base_currency; the scheme picker
    // already prevents a selection spanning two currencies.
    const baseCurrency = useMemo(() => {
        const currencies = new Set(
            formData.scheme_ids
                .map(id => schemes.find(scheme => scheme.id === id)?.base_currency)
                .filter((currency): currency is string => Boolean(currency))
        );
        return currencies.size === 1 ? [...currencies][0] : null;
    }, [formData.scheme_ids, schemes]);

    const isGlobal = isGlobalSelection(formData.scheme_ids, schemes);
    // Mutual funds and global listings have no traded-volume data to cap against.
    const volumeCapApplies = !isGlobal
        && getSchemeGroupValue(formData.scheme_ids, schemes) !== ORDER_CALC_TYPE_MUTUAL_FUND;
    const { orderConstraints } = formDefaults.buyOrderForm;
    const minOrderVal = formData.min_order_val
        ?? (isGlobal ? orderConstraints.global_min_order_val : orderConstraints.min_order_val);

    // The scheme's currency is the fallback because that is what the backend assumes
    // when no currency is submitted with the price.
    const limitPriceCurrency = livePrice?.currency_code ?? baseCurrency;

    // The price is placed in its own currency but sized against base-currency cash, so
    // the backend applies IBKR's live rate. Surfaced because it changes the
    // quantity, and fails the order outright when no rate exists.
    const needsFxForSizing = Boolean(
        limitPriceCurrency && baseCurrency && limitPriceCurrency !== baseCurrency
    );

    // A new company reverts the field to the live quote; a scheme change must not wipe a
    // price the operator typed.
    useEffect(() => {
        userEditedLimitPrice.current = false;
        setFormData(prev => ({ ...prev, limit_price: 0 }));
    }, [formData.company_id]);

    useEffect(() => {
        if (!formData.company_id) {
            setLivePrice(null);
            return;
        }
        let cancelled = false;
        setLivePriceLoading(true);
        marketDataStalenessAPI.getLivePrice(Number(formData.company_id))
            .then(data => {
                if (cancelled) return;
                setLivePrice(data);
                if (data.ltp != null && !userEditedLimitPrice.current) {
                    const quotedPrice = data.ltp;
                    setFormData(prev => ({ ...prev, limit_price: quotedPrice }));
                }
            })
            .catch(() => { if (!cancelled) setLivePrice(null); })
            .finally(() => { if (!cancelled) setLivePriceLoading(false); });
        return () => { cancelled = true; };
    }, [formData.company_id]);

    useEffect(() => {
        loadSupportingData();
    }, []);

    // Reload companies whenever selected schemes change (empty = all companies)
    useEffect(() => {
        let cancelled = false;
        const config = formData.scheme_ids.length > 0
            ? { params: { scheme_ids: formData.scheme_ids.join(',') } }
            : undefined;
        supportingDataApi.getOrderEligibleCompanies(config).then(data => {
            if (cancelled) return;
            setCompanies(data);
            setFormData(prev => prev.company_id && !data.some((c: Company) => c.id === prev.company_id)
                ? { ...prev, company_id: 0 }
                : prev
            );
        }).catch(err => {
            if (!cancelled) setError('Failed to load companies');
            console.error(err);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(formData.scheme_ids)]);

    const loadSupportingData = async () => {
        try {
            setDataLoading(true);
            const schemesData = await supportingDataApi.getSchemes();
            setSchemes(schemesData.filter(s => s.is_active));
        } catch (err) {
            setError('Failed to load form data');
            console.error('Error loading supporting data:', err);
        } finally {
            setDataLoading(false);
        }
    };
    const isEmpty = (value: any): boolean => {
        return (
            value === null ||
            value === undefined ||
            (typeof value === "string" && value.trim() === "") ||
            (typeof value === "number" && isNaN(value))
        );
    };
    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.scheme_ids.length) errors.scheme_ids = 'At least one scheme is required';
        if (!formData.company_id) errors.company_id = 'Company is required';
        if (isEmpty(formData.cl1)) errors.cl1 = 'CL1 is required';
        else if (formData.cl1 < 0 || formData.cl1 > 100) errors.cl1 = 'CL1 must be between 0 and 100';
        if (isEmpty(formData.cl2)) errors.cl2 = 'CL2 is required';
        else if (formData.cl2 < 0 || formData.cl2 > 100) errors.cl2 = 'CL2 must be between 0 and 100';
        if (isEmpty(formData.limit_price)) errors.limit_price = 'Limit price is required';
        else if (Number(formData.limit_price) <= 0) errors.limit_price = 'Limit price must be greater than 0';
        if (isEmpty(formData.max_holding)) errors.max_holding = 'Max holding is required';
        else if (formData.max_holding < 0 || formData.max_holding > 100) errors.max_holding = 'Max holding must be between 0 and 100';
        
        if (isEmpty(minOrderVal)) errors.min_order_val = 'Min order value is required';
        else if (Number(minOrderVal) < 0) errors.min_order_val = 'Min order value must be non-negative';
        if (isEmpty(formData.cash_utilization_buffer_pct)) errors.cash_utilization_buffer_pct = 'Cash utilization buffer is required';
        else if (formData.cash_utilization_buffer_pct < 0 || formData.cash_utilization_buffer_pct > 100)
            errors.cash_utilization_buffer_pct = 'Cash utilization buffer must be between 0 and 100';
        if (volumeCapApplies) {
            if (!formData.median_volume_pct && formData.median_volume_pct !== 0) errors.median_volume_pct = 'POV Cap is required';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        const numberFields = ['company_id', 'buy_method'];

        setFormData(prev => ({
            ...prev,
            [name]:
                name === 'apply_volume_cap'
                    ? value === 'yes'
                    : numberFields.includes(name) && type === 'number'
                        ? (value === '' ? 0 : parseInt(value))
                        : value
        }));

        if (name === 'limit_price') {
            userEditedLimitPrice.current = true;
        }

        if (name === 'cash_utilization_buffer_pct') {
            userEditedCashBuffer.current = true;
        }

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Mutual-fund schemes have a 0% cash utilization buffer default instead of the standard 2%.
    const isOnlyMutualFundSelected = (schemeIds: number[]): boolean =>
        schemeIds.length > 0 &&
        schemeIds.every(id => schemes.find(s => s.id === id)?.order_calc_type === ORDER_CALC_TYPE_MUTUAL_FUND);

    const cashUtilizationBufferPctFor = (schemeIds: number[]): number =>
        isOnlyMutualFundSelected(schemeIds) ? 0 : formDefaults.buyOrderForm.cashUtilization.cash_utilization_buffer_pct;

    const handleCompanyChange = (selected: SingleValue<SelectOption>) => {
        setFormData(prev => ({ ...prev, company_id: selected ? selected.value : 0 }));
        if (validationErrors.company_id) {
            setValidationErrors(prev => ({ ...prev, company_id: '' }));
        }
    };

    const handleSchemeIdsChange = (newSchemeIds: number[]) => {
        setFormData(prev => {
            // A newly added mutual fund scheme forces the volume cap off.
            const addedMutualFund = newSchemeIds.some(id =>
                !prev.scheme_ids.includes(id) &&
                schemes.find(s => s.id === id)?.order_calc_type === ORDER_CALC_TYPE_MUTUAL_FUND
            );
            return {
                ...prev,
                scheme_ids: newSchemeIds,
                // Clients belong to a scheme, so drop any the new scheme set no longer covers.
                client_ids: pruneClientIds(prev.client_ids, newSchemeIds, schemeClients),
                ...(addedMutualFund ? { apply_volume_cap: false } : {}),
                // Don't clobber a value the user typed in themselves.
                ...(userEditedCashBuffer.current
                    ? {}
                    : { cash_utilization_buffer_pct: cashUtilizationBufferPctFor(newSchemeIds) }),
            };
        });

        if (validationErrors.scheme_ids) {
            setValidationErrors(prev => ({...prev, scheme_ids: ''}));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Submit order for each selected scheme
            const {scheme_ids, client_ids, ...orderDataWithoutSchemes} = formData;

            const promises = scheme_ids.map(scheme_id =>
                ordersApi.submitBuyOrder({
                    ...orderDataWithoutSchemes,
                    min_order_val: minOrderVal,
                    apply_volume_cap: volumeCapApplies && formData.apply_volume_cap,
                    ...(limitPriceCurrency ? { currency_code: limitPriceCurrency } : {}),
                    scheme_id,
                    client_ids: clientWiseSchemeIds.includes(scheme_id)
                        ? clientIdsForScheme(scheme_id, client_ids, schemeClients)
                        : [],
                })
            );

            const results = await Promise.all(promises);
            const successCount = results.filter(r => r.status === 'success').length;

            navigate('/orders', {
                state: {
                    message: `Successfully submitted ${successCount} buy order(s) for ${formData.scheme_ids.length} scheme(s)`,
                    orderSubmitted: true,
                    activeTab: 'inprogress'
                }
            });
        } catch (err: unknown) {
            // api.ts re-throws error.response.data directly, so err IS the data object
            const raw = err as Record<string, unknown>;
            const errData = ((raw.response as { data?: Record<string, unknown> })?.data ?? raw) as Record<string, unknown>;
            const hasDetailOrError = errData.detail || errData.error;
            if (!hasDetailOrError && typeof errData === 'object') {
                // Assume field-keyed validation errors; DRF sends a list per field.
                const backendErrors: Record<string, string> = {};
                for (const key in errData) {
                    backendErrors[key] = joinFieldErrors(errData[key]);
                }
                setValidationErrors(backendErrors);
            } else {
                setError((errData.detail || errData.error) as string || 'Failed to calculate buy order');
            }
            console.error('Error calculating buy order:', err);
        } finally {
            setLoading(false);
        }
    };

    const companyOptions: SelectOption[] = companies.map(c => ({ value: c.id, label: `${c.name} (${c.symbol})` }));
    const selectedCompanyOption = companyOptions.find(o => o.value === formData.company_id) ?? null;

    const livePriceHint = formData.company_id > 0
        ? livePriceLoading
            ? 'Fetching live price…'
            : livePrice?.ltp != null
                ? <>LTP: <strong>{formatPrice(
                        livePrice.ltp,
                        livePrice.currency_code,
                        livePrice.currency_code !== 'INR',
                    )}</strong>
                    {/* An unlabelled price cannot be used safely. */}
                    {!livePrice.currency_code && <> <FormHintError>currency unknown</FormHintError></>}
                    {livePrice.ltp_timestamp && <> as of <strong>{formatDateTime(livePrice.ltp_timestamp)}</strong></>}
                    {livePrice.exchange_code && <> on <strong>{livePrice.exchange_code}</strong></>}
                    {needsFxForSizing && (
                        <> <FormHintError>
                            quoted in {limitPriceCurrency}; sized against {baseCurrency} at the IBKR rate
                        </FormHintError></>
                    )}
                  </>
                : livePrice != null
                    ? <FormHintError>Current price not found</FormHintError>
                    : null
        : null;

    return (
        <FormCard
            title="Create Buy Order"
            subtitle="Generate buy orders across the selected schemes."
            backLabel="← Back to Orders"
            onBack={() => navigate('/orders')}
            errorBanner={error ?? undefined}
            submitLabel={loading ? 'Submitting…' : 'Submit Buy Order'}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/orders')}
            submitting={loading}
            loading={dataLoading}
            loadingMessage="Loading form data…"
            variant="page"
        >
            <FormSection title="Order Details">
                <FormGrid>
                    <OrderSchemeSelector
                        schemes={schemes}
                        value={formData.scheme_ids}
                        onChange={handleSchemeIdsChange}
                        error={validationErrors.scheme_ids}
                        disabled={loading}
                    />

                    {clientWiseSchemeIds.length > 0 && (
                        <OrderClientSelector
                            schemes={schemes}
                            schemeIds={clientWiseSchemeIds}
                            schemeClients={schemeClients}
                            value={formData.client_ids}
                            onChange={clientIds => {
                                setFormData(prev => ({ ...prev, client_ids: clientIds }));
                                if (validationErrors.client_ids) {
                                    setValidationErrors(prev => ({ ...prev, client_ids: '' }));
                                }
                            }}
                            loading={clientsLoading}
                            hint="Leave empty to order for every eligible client. A scheme with none of its clients selected orders for all of them."
                            error={validationErrors.client_ids}
                            disabled={loading}
                        />
                    )}

                    <FormField label="Company" required error={validationErrors.company_id}>
                        <FormReactSelect
                            options={companyOptions}
                            value={selectedCompanyOption}
                            onChange={handleCompanyChange}
                            placeholder="Select company…"
                            hasError={Boolean(validationErrors.company_id)}
                            isDisabled={loading}
                        />
                    </FormField>

                    <FormField label="Buy Method" required>
                        <FormSelect
                            name="buy_method"
                            value={formData.buy_method}
                            onChange={handleInputChange}
                            disabled={loading}
                        >
                            <option value={1}>VWAP</option>
                            <option value={2}>&lt;10% volume</option>
                            <option value={3}>Incognito</option>
                        </FormSelect>
                    </FormField>
                </FormGrid>
            </FormSection>

            <FormSection title="Target Parameters">
                <FormGrid>
                    <FormField label="CL1 (%)" required error={validationErrors.cl1}>
                        <FormInput
                            type="number" name="cl1" value={formData.cl1} onChange={handleInputChange}
                            step="0.01" min="0" max="100"
                            hasError={Boolean(validationErrors.cl1)} disabled={loading}
                        />
                    </FormField>

                    <FormField label="CL2 (%)" required error={validationErrors.cl2}>
                        <FormInput
                            type="number" name="cl2" value={formData.cl2} onChange={handleInputChange}
                            step="0.01" min="0" max="100"
                            hasError={Boolean(validationErrors.cl2)} disabled={loading}
                        />
                    </FormField>

                    <FormField label="Increase To 1 (%)">
                        <FormInput
                            type="number" name="increase_to_1" value={formData.increase_to_1}
                            onChange={handleInputChange} step="0.1" disabled={loading}
                        />
                    </FormField>

                    <FormField label="Increment Max 1">
                        <FormInput
                            type="number" name="increment_max_1" value={formData.increment_max_1}
                            onChange={handleInputChange} step="0.1" disabled={loading}
                        />
                    </FormField>

                    <FormField label="Increase To 2 (%)">
                        <FormInput
                            type="number" name="increase_to_2" value={formData.increase_to_2}
                            onChange={handleInputChange} step="0.1" disabled={loading}
                        />
                    </FormField>

                    <FormField label="Increment Max 2">
                        <FormInput
                            type="number" name="increment_max_2" value={formData.increment_max_2}
                            onChange={handleInputChange} step="0.1" disabled={loading}
                        />
                    </FormField>

                    <FormField label="Increase By 1 (%)">
                        <FormInput
                            type="number" name="increase_by_1" value={formData.increase_by_1}
                            onChange={handleInputChange} step="0.1" disabled={loading}
                        />
                    </FormField>

                    <FormField label="Increase By 2 (%)">
                        <FormInput
                            type="number" name="increase_by_2" value={formData.increase_by_2}
                            onChange={handleInputChange} step="0.1" disabled={loading}
                        />
                    </FormField>
                </FormGrid>
            </FormSection>

            <FormSection title="Limits and Constraints">
                <FormGrid>
                    <FormField
                        label={`Limit Price${limitPriceCurrency ? ` (${limitPriceCurrency})` : ''}`}
                        required
                        error={validationErrors.limit_price}
                        hint={livePriceHint}
                    >
                        <FormInput
                            type="number" name="limit_price" value={formData.limit_price}
                            onChange={handleInputChange} step="0.01" min="0.01"
                            hasError={Boolean(validationErrors.limit_price)} disabled={loading}
                        />
                    </FormField>

                    <FormField label="Max Holding (%)" required error={validationErrors.max_holding}>
                        <FormInput
                            type="number" name="max_holding" value={formData.max_holding}
                            onChange={handleInputChange} step="0.01" min="0" max="100"
                            hasError={Boolean(validationErrors.max_holding)} disabled={loading}
                        />
                    </FormField>

                    {/* Compared against order amounts, so always base currency. */}
                    <FormField
                        label={`Min Order Value${baseCurrency ? ` (${baseCurrency})` : ''}`}
                        required
                        error={validationErrors.min_order_val}
                    >
                        <FormInput
                            type="number" name="min_order_val" value={minOrderVal}
                            onChange={handleInputChange} step="0.01" min="0"
                            hasError={Boolean(validationErrors.min_order_val)} disabled={loading}
                        />
                    </FormField>

                    <FormField
                        label="Cash Utilization Buffer (%)"
                        required
                        error={validationErrors.cash_utilization_buffer_pct}
                    >
                        <FormInput
                            type="number" name="cash_utilization_buffer_pct"
                            value={formData.cash_utilization_buffer_pct}
                            onChange={handleInputChange} step="1" min="0" max="100"
                            hasError={Boolean(validationErrors.cash_utilization_buffer_pct)} disabled={loading}
                        />
                    </FormField>

                    {volumeCapApplies && (
                        <>
                            <FormField label="POV Cap (%)" required error={validationErrors.median_volume_pct}>
                                <FormInput
                                    type="number" name="median_volume_pct" value={formData.median_volume_pct}
                                    onChange={handleInputChange} min="0" step="any"
                                    hasError={Boolean(validationErrors.median_volume_pct)} disabled={loading}
                                />
                            </FormField>

                            <FormField label="Apply Volume Cap" required error={validationErrors.apply_volume_cap}>
                                <FormSelect
                                    name="apply_volume_cap"
                                    value={formData.apply_volume_cap ? 'yes' : 'no'}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                >
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </FormSelect>
                            </FormField>
                        </>
                    )}
                </FormGrid>
            </FormSection>
        </FormCard>
    );
};

export default BuyOrderForm;
